import isFunction from 'lodash/isFunction';
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { UiButton, UiButtonProps } from 'src/components/UI/Button';

export type UiModalCommonButtons = {
  close: () => void;

  onSuccess?: () => void;
  onCancel?: () => void;
  successLoading?: boolean;
  cancelLoading?: boolean;

  cancelBtnText?: string;
  successBtnText?: string;
  successBtnOnClick?: () => void;
  cancelBtnOnClick?: () => void;
  successBtnClassProperty?: keyof Pick<
    UiButtonProps,
    'primary' | 'primaryNew' | 'secondary' | 'secondaryNew' | 'secondaryBordered'
  >;
  cancelBtnClassProperty?: keyof Pick<
    UiButtonProps,
    'primary' | 'primaryNew' | 'secondary' | 'secondaryNew' | 'secondaryBordered'
  >;

  successDisabled?: boolean;
  successTooltipText?: string;

  onlySuccessBtn?: boolean;
  onlyCancelBtn?: boolean;

  reverse?: boolean;

  canCancelCloseModal?: boolean;
};

/**
 * common footer buttons (cancel/ok) for UiModal
 */
export const UiModalCommonButtons: React.FC<UiModalCommonButtons> = memo(
  ({
    close,
    onSuccess,
    onCancel,
    successLoading,
    cancelLoading,
    cancelBtnText,
    successBtnText,
    successBtnOnClick,
    cancelBtnOnClick,
    successDisabled,
    successTooltipText,
    reverse,
    successBtnClassProperty = 'primaryNew',
    cancelBtnClassProperty = 'secondaryBordered',
    onlySuccessBtn = false,
    onlyCancelBtn = false,
    canCancelCloseModal = true,
  }: UiModalCommonButtons) => {
    const { t } = useTranslation();

    const onCancelLocal = useCallback(() => {
      if (isFunction(onCancel)) {
        onCancel();
      }

      if (canCancelCloseModal) {
        close();
      }
    }, [canCancelCloseModal, close, onCancel]);

    const onSuccessLocal = useCallback(() => {
      if (isFunction(onSuccess)) {
        if (!successDisabled) {
          onSuccess();
        }
      } else {
        close();
      }
    }, [close, onSuccess, successDisabled]);

    const handleCancelClick = useCallback(() => {
      if (isFunction(cancelBtnOnClick)) {
        cancelBtnOnClick();
      }

      onCancelLocal();
    }, [onCancelLocal, cancelBtnOnClick]);

    let buttons = [
      <UiButton
        key="secondary"
        loading={cancelLoading}
        primary={cancelBtnClassProperty === 'primary'}
        primaryNew={cancelBtnClassProperty === 'primaryNew'}
        secondary={cancelBtnClassProperty === 'secondary'}
        secondaryNew={cancelBtnClassProperty === 'secondaryNew'}
        secondaryBordered={cancelBtnClassProperty === 'secondaryBordered'}
        onClick={handleCancelClick}
        dataTestId="cancel"
      >
        {cancelBtnText || t('common.cancel')}
      </UiButton>,
      <UiButton
        key="primary"
        loading={successLoading}
        disabled={successDisabled}
        onClick={() => {
          if (isFunction(successBtnOnClick)) {
            successBtnOnClick();
          }
          onSuccessLocal();
        }}
        tooltipText={successTooltipText}
        dataTestId="submit"
        // omg
        primary={successBtnClassProperty === 'primary'}
        primaryNew={successBtnClassProperty === 'primaryNew'}
        secondary={successBtnClassProperty === 'secondary'}
        secondaryNew={successBtnClassProperty === 'secondaryNew'}
        secondaryBordered={successBtnClassProperty === 'secondaryBordered'}
      >
        {successBtnText || t('common.ok')}
      </UiButton>,
    ];

    if (onlySuccessBtn) {
      buttons = [buttons[1]];
    }

    if (onlyCancelBtn) {
      buttons = [buttons[0]];
    }

    return <>{reverse ? buttons.reverse() : buttons}</>;
  }
);
